import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { getContractApiUrl } from '../utils/apiUrls';
import { portalApiTokenOptions } from '../utils/authSession';
import {
  formGenerationUpgradeMessage,
  nearestFormGenerationPlan,
  planIncludesFormGeneration,
  userCanGenerateForms,
} from '../utils/formGenerationAccess';

export default function useViewerSubscription() {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const [profile, setProfile] = useState(null);
  const [access, setAccess] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchViewer = async () => {
      if (!isAuthenticated || !user?.email) {
        setProfile(null);
        setAccess(null);
        setLoaded(true);
        return;
      }
      setLoaded(false);
      try {
        const headers = {};
        try {
          const token = await getAccessTokenSilently(portalApiTokenOptions());
          if (token) headers.Authorization = `Bearer ${token}`;
        } catch (_) {
          /* interceptor may still attach a token */
        }
        const email = encodeURIComponent(user.email);
        const [profileResult, accessResult] = await Promise.allSettled([
          axios.get(`${getContractApiUrl()}/user/profile?email=${email}`, { headers }),
          axios.get(`${getContractApiUrl()}/user/access-status?email=${email}`, { headers }),
        ]);
        if (!cancelled) {
          setProfile(
            profileResult.status === 'fulfilled' ? profileResult.value.data : null,
          );
          setAccess(
            accessResult.status === 'fulfilled' ? (accessResult.value.data || null) : null,
          );
        }
      } catch (err) {
        if (!cancelled) {
          setProfile(null);
          setAccess(null);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchViewer();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.email, getAccessTokenSilently]);

  // Access-status flags are the source of truth when the request succeeded.
  const accessReady = access != null && typeof access === 'object';
  const guestKnown = accessReady && typeof access.is_transaction_guest === 'boolean';
  const isGuest = guestKnown
    ? access.is_transaction_guest === true
    : profile?.is_transaction_guest === true;
  const canCreateDeals = accessReady && access.can_create_deals === true;
  const productType = profile?.product_type || null;
  const canGenerateForms = userCanGenerateForms({
    canGenerateForms: accessReady && typeof access.can_generate_forms === 'boolean'
      ? access.can_generate_forms
      : undefined,
    isSuperAdmin: profile?.is_super_admin === true,
    isTransactionGuest: isGuest,
    isPayingSubscriber: profile?.is_paying_subscriber === true,
    paymentUserType: profile?.payment_user_type,
    productType,
  });
  const formGenerationPlanBlocked = !planIncludesFormGeneration(productType);
  const { currentPlanName, upgradePlanName } = nearestFormGenerationPlan({
    productType,
    productDisplay: profile?.product_display,
  });
  const formGenerationUpgradeCopy = formGenerationUpgradeMessage({
    currentPlanName,
    upgradePlanName,
  });
  // A missing/failed profile must not hide the button; the resolve API rejects guests.
  const canResolveCompliance = Boolean(isAuthenticated)
    && loaded
    && (
      profile?.is_super_admin === true
      || profile?.is_paying_subscriber === true
      || isGuest !== true
    );
  return {
    profile,
    access,
    loaded,
    accessReady,
    isGuest,
    canCreateDeals,
    canResolveCompliance,
    canGenerateForms,
    formGenerationPlanBlocked,
    currentPlanName,
    upgradePlanName,
    formGenerationUpgradeCopy,
  };
}
